/**
 * DailyReportPage.jsx — Admin / Principal daily campus checklist
 *
 * - Loads the campus assigned to the logged-in user
 * - Fetches today's 10 deterministic questions from backend
 * - Questions at positions 2, 5, 8 require a photo (mandatory)
 * - Each question has Yes / No / N/A answer buttons + optional text note
 * - Photo upload happens immediately on file selection (per question)
 * - On submit: text answers are POSTed; incomplete required photos block submit
 * - Once submitted, shows a read-only view of today's answers
 */

import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  MdCheckCircle,
  MdRadioButtonUnchecked,
  MdCameraAlt,
  MdClose,
  MdRefresh,
  MdSend,
  MdWarning,
  MdCalendarToday,
} from "react-icons/md";
import { FiClipboard } from "react-icons/fi";
import { API_BASE_URL, getAuthHeaders, getJsonAuthHeaders } from "../../../utils/api";
import "./daily_report.css";

const TODAY_ISO = new Date().toISOString().slice(0, 10);

const fmtDateLong = (iso) => {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString("en-PK", {
    weekday: "long", day: "numeric", month: "long", year: "numeric",
  });
};

// Build the full photo API URL from a stored path
const photoApiUrl = (path) =>
  path ? `${API_BASE_URL}/api/daily-report/photo/${path.replace(/\\/g, "/")}` : null;

// Hook: fetch a photo with auth headers and return a blob URL
function useAuthPhotoUrl(apiUrl) {
  const [blobUrl, setBlobUrl] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!apiUrl) { setBlobUrl(null); return; }
    let cancelled = false;
    let currentBlob = null;
    setLoading(true);
    (async () => {
      try {
        const res = await fetch(apiUrl, { headers: getAuthHeaders() });
        if (!res.ok || cancelled) return;
        const blob = await res.blob();
        if (cancelled) return;
        const url = URL.createObjectURL(blob);
        currentBlob = url;
        setBlobUrl(url);
      } catch (_) {}
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => {
      cancelled = true;
      if (currentBlob) URL.revokeObjectURL(currentBlob);
    };
  }, [apiUrl]);

  return { blobUrl, loading };
}

// ── Answer chip used in read-only view ──────────────────────────────────────
function AnswerChip({ answer }) {
  const low = (answer || "").toLowerCase();
  const cls = low === "yes" ? "yes" : low === "no" ? "no" : low === "na" ? "na" : "text";
  const label = low === "na" ? "N/A" : answer || "—";
  return <span className={`dr-answer-chip ${cls}`}>{label}</span>;
}

// ── Lightbox for viewing photos full-size ───────────────────────────────────
function Lightbox({ src, onClose }) {
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div className="dr-lightbox-overlay" onClick={onClose}>
      <button className="dr-lightbox-close" onClick={onClose} aria-label="Close">
        <MdClose />
      </button>
      <img
        src={src}
        alt="Report photo"
        className="dr-lightbox-img"
        onClick={(e) => e.stopPropagation()}
      />
    </div>
  );
}

// ── Single answer row in submitted view ─────────────────────────────────────
function SubmittedAnswerRow({ answer, index }) {
  const [lightbox, setLightbox] = useState(null);
  const apiUrl = photoApiUrl(answer.photo_path);
  const { blobUrl } = useAuthPhotoUrl(apiUrl);

  return (
    <div className="dr-answer-row-view">
      <div className="dr-view-num">{index + 1}</div>
      <div>
        <div className="dr-view-question-text">{answer.text}</div>
        <div className="dr-view-category">{answer.category}</div>
        {blobUrl && (
          <div className="dr-thumb-wrap" style={{ marginTop: ".5rem" }}>
            <img
              src={blobUrl}
              alt="Uploaded"
              className="dr-thumb"
              onClick={() => setLightbox(blobUrl)}
              title="Click to enlarge"
            />
          </div>
        )}
        {answer.photo_path && !blobUrl && (
          <div style={{ fontSize: ".74rem", color: "var(--dr-gray-400)", marginTop: ".35rem" }}>
            Loading photo…
          </div>
        )}
      </div>
      <div>
        <AnswerChip answer={answer.answer} />
        {answer.requires_photo && (
          <div style={{ marginTop: ".25rem" }}>
            {answer.photo_path ? (
              <span className="dr-photo-done-chip">
                <MdCheckCircle size={12} /> Photo ✓
              </span>
            ) : (
              <span className="dr-photo-badge">
                <MdCameraAlt size={11} /> Photo required
              </span>
            )}
          </div>
        )}
      </div>
      {lightbox && <Lightbox src={lightbox} onClose={() => setLightbox(null)} />}
    </div>
  );
}

// ── Read-only submitted report view ────────────────────────────────────────
function SubmittedView({ report, campusName, onRefresh }) {
  const answers = report.answers || [];

  return (
    <div>
      <div className="dr-submitted-banner">
        <MdCheckCircle className="dr-submitted-banner-icon" />
        <div>
          <h4>Report Submitted</h4>
          <p>
            Today's campus report for <strong>{campusName}</strong> has been submitted successfully.
            The superadmin can now view it.
          </p>
        </div>
        <button className="dr-btn dr-btn-outline dr-btn-sm" onClick={onRefresh} style={{ marginLeft: "auto" }}>
          <MdRefresh size={14} /> Refresh
        </button>
      </div>

      <div className="dr-report-view">
        {answers.map((a, i) => (
          <SubmittedAnswerRow key={a.question_id} answer={a} index={i} />
        ))}
      </div>
    </div>
  );
}

// ── Single question card with answer + optional photo upload ────────────────
function QuestionCard({
  question, index, answer, onAnswerChange, photoState, onPhotoUpload,
  campusId, reportDate, submitted,
}) {
  const fileRef = useRef();
  const { uploading, uploaded, previewUrl } = photoState;

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = "";
    await onPhotoUpload(question.question_id || question.id, file);
  };

  const answerClass = (val) => {
    if (!answer.answer) return "";
    const low = answer.answer.toLowerCase();
    if (low === val) return `selected-${val}`;
    return "";
  };

  const isAnswered = Boolean(answer.answer);
  const photoDone  = uploaded || Boolean(answer.photo_path);

  // If there's a fresh local preview (just uploaded this session) use it directly.
  // If there's a saved path but no local preview, fetch it with auth.
  const savedPhotoApiUrl = (!previewUrl && answer.photo_path)
    ? photoApiUrl(answer.photo_path)
    : null;
  const { blobUrl: savedBlobUrl } = useAuthPhotoUrl(savedPhotoApiUrl);

  // Final display URL: fresh local preview takes priority over saved blob
  const photoDisplayUrl = previewUrl || savedBlobUrl;

  const [lightbox, setLightbox] = useState(null);

  let cardCls = "dr-question-card";
  if (isAnswered && (!question.requires_photo || photoDone)) cardCls += " answered";
  if (question.requires_photo && !photoDone) cardCls += " needs-photo";
  if (submitted && question.requires_photo && !photoDone) cardCls += " unanswered-required";

  return (
    <div className={cardCls}>
      <div className="dr-question-top">
        <div className="dr-question-num">{index + 1}</div>
        <div className="dr-question-text">{question.text}</div>
        <div style={{ display: "flex", gap: ".35rem", flexWrap: "wrap", alignItems: "center" }}>
          <span className="dr-category-chip">{question.category}</span>
          {question.requires_photo && (
            <span className="dr-photo-badge">
              <MdCameraAlt size={11} /> Photo required
            </span>
          )}
        </div>
      </div>

      {/* Answer buttons */}
      <div className="dr-answer-row">
        {["yes", "no", "na"].map((val) => (
          <button
            key={val}
            type="button"
            className={`dr-answer-btn ${answerClass(val)}`}
            onClick={() => onAnswerChange(question.question_id || question.id, "answer", val)}
          >
            {val === "na" ? "N/A" : val.charAt(0).toUpperCase() + val.slice(1)}
          </button>
        ))}
      </div>

      {/* Optional note */}
      <textarea
        className="dr-note-input"
        placeholder="Add a note (optional)..."
        rows={1}
        value={answer.note || ""}
        onChange={(e) => onAnswerChange(question.question_id || question.id, "note", e.target.value)}
        style={{ display: "none" }}
      />

      {/* Photo upload */}
      {question.requires_photo && (
        <div className="dr-photo-area">
          <input
            type="file"
            ref={fileRef}
            style={{ display: "none" }}
            accept="image/jpeg,image/png,image/webp"
            onChange={handleFile}
          />

          {uploading && (
            <div className="dr-photo-uploading">
              <span className="spinner" style={{
                width: 14, height: 14, border: "2px solid #ddd",
                borderTop: "2px solid var(--dr-navy)", borderRadius: "50%",
                display: "inline-block", animation: "spin 0.7s linear infinite",
              }} />
              Uploading photo…
            </div>
          )}

          {!uploading && photoDisplayUrl && (
            <div>
              <div className="dr-photo-preview-wrap">
                <img
                  src={photoDisplayUrl}
                  alt="Uploaded"
                  className="dr-photo-preview"
                  style={{ cursor: "pointer" }}
                  onClick={() => setLightbox(photoDisplayUrl)}
                  title="Click to enlarge"
                />
                {!answer.photo_path && (
                  <button
                    type="button"
                    className="dr-photo-remove"
                    onClick={() => onPhotoUpload(question.question_id || question.id, null)}
                    aria-label="Remove photo"
                  >
                    <MdClose size={12} />
                  </button>
                )}
              </div>
              <div className="dr-photo-done-chip" style={{ marginTop: ".4rem" }}>
                <MdCheckCircle size={12} /> Photo uploaded — click to view
              </div>
              {lightbox && <Lightbox src={lightbox} onClose={() => setLightbox(null)} />}
            </div>
          )}

          {!uploading && !photoDisplayUrl && (
            <button
              type="button"
              className="dr-photo-upload-btn"
              onClick={() => fileRef.current?.click()}
            >
              <MdCameraAlt size={16} /> Upload Photo
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ── Main DailyReportPage ────────────────────────────────────────────────────
export default function DailyReportPage({ showToast: extToast }) {
  const role = (localStorage.getItem("als_role") || "").toLowerCase();

  // local toast fallback
  const [localToast, setLocalToast] = useState(null);
  const showToast = useCallback(
    (type, title, msg) => {
      if (extToast) { extToast(type, title, msg); return; }
      setLocalToast({ type, title, msg });
      setTimeout(() => setLocalToast(null), 3500);
    },
    [extToast]
  );

  // Campus selection (for users with multiple campuses)
  const [campuses, setCampuses]     = useState([]);
  const [campusId, setCampusId]     = useState(null);
  const [campusName, setCampusName] = useState("");

  // Questions & answers
  const [questions, setQuestions]   = useState([]);
  const [answers, setAnswers]       = useState({});   // { qId: { answer, note, photo_path } }
  const [photoStates, setPhotoStates] = useState({}); // { qId: { uploading, uploaded, previewUrl } }

  // UI state
  const [loading, setLoading]           = useState(false);
  const [submitting, setSubmitting]     = useState(false);
  const [submitted, setSubmitted]       = useState(false);
  const [existingReport, setExistingReport] = useState(null);
  const [triedSubmit, setTriedSubmit]   = useState(false);

  // Load campuses for this user
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/campuses`, { headers: getAuthHeaders() });
        if (res.ok) {
          const data = await res.json();
          const list = Array.isArray(data) ? data : [];
          setCampuses(list);
          if (list.length === 1) {
            setCampusId(list[0].id);
            setCampusName(list[0].name);
          }
        }
      } catch (err) { console.error(err); }
    })();
  }, []);

  // Fetch today's questions whenever campusId changes
  const fetchQuestions = useCallback(async () => {
    if (!campusId) return;
    setLoading(true);
    try {
      const res = await fetch(
        `${API_BASE_URL}/api/daily-report/questions?campus_id=${campusId}&report_date=${TODAY_ISO}`,
        { headers: getAuthHeaders() }
      );
      if (!res.ok) throw new Error("Failed to load questions");
      const data = await res.json();

      setQuestions(data.questions || []);

      if (data.already_submitted && data.existing_report) {
        setExistingReport(data.existing_report);
        setSubmitted(true);
        // Pre-populate answers from existing report
        const aMap = {};
        for (const a of (data.existing_report.answers || [])) {
          aMap[a.question_id] = { answer: a.answer, note: a.note || "", photo_path: a.photo_path };
        }
        setAnswers(aMap);
      } else {
        // Not yet fully submitted — restore any partial progress (photos already uploaded)
        setExistingReport(null);
        setSubmitted(false);
        if (data.existing_report) {
          // Pre-populate partially saved answers and photo paths
          const aMap = {};
          for (const a of (data.existing_report.answers || [])) {
            aMap[a.question_id] = {
              answer: a.answer || "",
              note: a.note || "",
              photo_path: a.photo_path || null,
            };
          }
          setAnswers(aMap);
          // Mark photoStates as uploaded for questions that already have a photo
          const pMap = {};
          for (const a of (data.existing_report.answers || [])) {
            if (a.photo_path) {
              pMap[a.question_id] = { uploading: false, uploaded: true, previewUrl: null };
            }
          }
          setPhotoStates(pMap);
        } else {
          setAnswers({});
          setPhotoStates({});
        }
      }
    } catch (err) {
      showToast("error", "Error", err.message);
    } finally {
      setLoading(false);
    }
  }, [campusId, showToast]);

  useEffect(() => { fetchQuestions(); }, [fetchQuestions]);

  // Answer change handler
  const handleAnswerChange = (qId, field, val) => {
    setAnswers((prev) => ({
      ...prev,
      [qId]: { ...(prev[qId] || {}), [field]: val },
    }));
  };

  // Photo upload handler — uploads immediately, stores path in answers
  const handlePhotoUpload = useCallback(async (qId, file) => {
    if (!file) {
      // Remove photo (local only, not yet saved)
      setPhotoStates((prev) => ({ ...prev, [qId]: { uploading: false, uploaded: false, previewUrl: null } }));
      return;
    }

    // Validate type
    if (!file.type.startsWith("image/")) {
      showToast("error", "Invalid File", "Only image files are allowed (JPG, PNG, WebP).");
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      showToast("error", "File Too Large", "Maximum image size is 8 MB.");
      return;
    }

    // Local preview immediately
    const objectUrl = URL.createObjectURL(file);
    setPhotoStates((prev) => ({
      ...prev,
      [qId]: { uploading: true, uploaded: false, previewUrl: objectUrl },
    }));

    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await fetch(
        `${API_BASE_URL}/api/daily-report/upload-photo?campus_id=${campusId}&report_date=${TODAY_ISO}&question_id=${qId}`,
        { method: "POST", headers: getAuthHeaders(), body: fd }
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Upload failed");
      }
      const data = await res.json();

      // Find photo_path from the returned report answers
      const savedAnswer = (data.report?.answers || []).find((a) => a.question_id === qId);
      const savedPath = savedAnswer?.photo_path || null;

      setPhotoStates((prev) => ({
        ...prev,
        [qId]: { uploading: false, uploaded: true, previewUrl: objectUrl },
      }));
      setAnswers((prev) => ({
        ...prev,
        [qId]: { ...(prev[qId] || {}), photo_path: savedPath },
      }));

      showToast("success", "Photo Uploaded", "Photo saved successfully.");
    } catch (err) {
      URL.revokeObjectURL(objectUrl);
      setPhotoStates((prev) => ({
        ...prev,
        [qId]: { uploading: false, uploaded: false, previewUrl: null },
      }));
      showToast("error", "Upload Failed", err.message);
    }
  }, [campusId, showToast]);

  // Progress calculation
  const answeredCount  = questions.filter((q) => answers[q.question_id || q.id]?.answer).length;
  const totalQuestions = questions.length;
  const requiredPhotoQs = questions.filter((q) => q.requires_photo);
  const photosDone     = requiredPhotoQs.filter((q) => {
    const qId = q.question_id || q.id;
    return answers[qId]?.photo_path || photoStates[qId]?.uploaded;
  }).length;
  const allPhotosReady = photosDone === requiredPhotoQs.length;

  // Submit
  const handleSubmit = async () => {
    setTriedSubmit(true);

    if (answeredCount < totalQuestions) {
      showToast("error", "Incomplete", `Please answer all ${totalQuestions} questions before submitting.`);
      return;
    }
    if (!allPhotosReady) {
      showToast("error", "Photos Required", `Please upload photos for the ${requiredPhotoQs.length} required questions.`);
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        campus_id:   campusId,
        report_date: TODAY_ISO,
        answers: questions.map((q) => {
          const qId = q.question_id || q.id;
          return {
            question_id: qId,
            position:    q.position,
            answer:      (answers[qId]?.answer || ""),
          };
        }),
      };

      const res = await fetch(`${API_BASE_URL}/api/daily-report/submit`, {
        method: "POST",
        headers: getJsonAuthHeaders(),
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Submission failed");
      }
      const saved = await res.json();
      setExistingReport(saved);
      setSubmitted(true);
      showToast("success", "Report Submitted", "Today's daily report has been saved successfully.");
    } catch (err) {
      showToast("error", "Error", err.message);
    } finally {
      setSubmitting(false);
    }
  };

  // Group questions by section for display
  const normalQuestions = questions.filter((q) => !q.requires_photo);
  const photoQuestions  = questions.filter((q) => q.requires_photo);

  return (
    <div className="dr-page dashboard-content">

      {/* ── Header ── */}
      <div className="dr-page-header">
        <div className="dr-page-header-left">
          <div className="dr-page-icon"><FiClipboard size={22} /></div>
          <div>
            <h2 className="dr-page-title">Daily Campus Report</h2>
            <p className="dr-page-sub">
              Complete today's checklist. Photos are required for highlighted questions.
            </p>
          </div>
        </div>
        <div className="dr-header-actions">
          <div className="dr-date-pill">
            <MdCalendarToday size={14} />
            {fmtDateLong(TODAY_ISO)}
          </div>
          <button className="dr-btn dr-btn-outline dr-btn-sm" onClick={fetchQuestions} disabled={loading}>
            <MdRefresh size={15} /> Refresh
          </button>
        </div>
      </div>

      {/* ── Campus selector (if multiple campuses) ── */}
      {campuses.length > 1 && (
        <div className="dr-campus-select-card">
          <h3>Select Campus</h3>
          <div className="dr-campus-select-row">
            <select
              className="dr-select"
              value={campusId || ""}
              onChange={(e) => {
                const id = parseInt(e.target.value, 10);
                setCampusId(id);
                const c = campuses.find((c) => c.id === id);
                setCampusName(c?.name || "");
              }}
            >
              <option value="">-- Select a campus --</option>
              {campuses.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>
        </div>
      )}

      {!campusId && (
        <div className="dr-empty">
          <div className="dr-empty-icon"><FiClipboard /></div>
          <h3>No campus assigned</h3>
          <p>Please contact your superadmin to assign a campus to your account.</p>
        </div>
      )}

      {campusId && loading && (
        <div className="dr-loading">Loading today's questions…</div>
      )}

      {/* ── Already submitted — read-only view ── */}
      {campusId && !loading && submitted && existingReport && (
        <SubmittedView
          report={existingReport}
          campusName={campusName}
          onRefresh={fetchQuestions}
        />
      )}

      {/* ── Active form ── */}
      {campusId && !loading && !submitted && questions.length > 0 && (
        <div>
          {/* Progress */}
          <div className="dr-progress-label">
            <span>{answeredCount} of {totalQuestions} answered</span>
            <span>{photosDone} of {requiredPhotoQs.length} photos uploaded</span>
          </div>
          <div className="dr-progress-wrap">
            <div
              className="dr-progress-fill"
              style={{ width: `${Math.round((answeredCount / totalQuestions) * 100)}%` }}
            />
          </div>

          {/* Standard questions */}
          {normalQuestions.length > 0 && (
            <>
              <div className="dr-section-divider">General Questions</div>
              {normalQuestions.map((q, i) => {
                const qId = q.question_id || q.id;
                return (
                  <QuestionCard
                    key={qId}
                    question={q}
                    index={questions.indexOf(q)}
                    answer={answers[qId] || {}}
                    onAnswerChange={handleAnswerChange}
                    photoState={photoStates[qId] || {}}
                    onPhotoUpload={handlePhotoUpload}
                    campusId={campusId}
                    reportDate={TODAY_ISO}
                    submitted={triedSubmit}
                  />
                );
              })}
            </>
          )}

          {/* Photo-required questions */}
          {photoQuestions.length > 0 && (
            <>
              <div className="dr-section-divider">
                <MdCameraAlt size={14} /> Photo Evidence Required
              </div>
              {photoQuestions.map((q) => {
                const qId = q.question_id || q.id;
                return (
                  <QuestionCard
                    key={qId}
                    question={q}
                    index={questions.indexOf(q)}
                    answer={answers[qId] || {}}
                    onAnswerChange={handleAnswerChange}
                    photoState={photoStates[qId] || {}}
                    onPhotoUpload={handlePhotoUpload}
                    campusId={campusId}
                    reportDate={TODAY_ISO}
                    submitted={triedSubmit}
                  />
                );
              })}
            </>
          )}

          {/* Submit bar */}
          <div className="dr-submit-bar">
            <div>
              <div className="dr-submit-bar-info">
                <strong>{answeredCount}/{totalQuestions}</strong> answered ·{" "}
                <strong>{photosDone}/{requiredPhotoQs.length}</strong> photos uploaded
              </div>
              {triedSubmit && (!allPhotosReady || answeredCount < totalQuestions) && (
                <div className="dr-submit-warning">
                  <MdWarning size={14} />
                  {!allPhotosReady
                    ? `${requiredPhotoQs.length - photosDone} required photo(s) missing`
                    : `${totalQuestions - answeredCount} question(s) unanswered`}
                </div>
              )}
            </div>
            <button
              className="dr-btn dr-btn-primary"
              onClick={handleSubmit}
              disabled={submitting}
            >
              <MdSend size={15} />
              {submitting ? "Submitting…" : "Submit Report"}
            </button>
          </div>
        </div>
      )}

      {/* Spinner keyframe */}
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>

      {/* Local toast */}
      {localToast && (
        <div
          className={`toast ${localToast.type}`}
          style={{ position: "fixed", bottom: "1.5rem", right: "1.5rem", zIndex: 9999, minWidth: 280 }}
        >
          <div style={{ padding: ".9rem 1.2rem" }}>
            <strong>{localToast.title}</strong>
            <p style={{ margin: ".2rem 0 0", fontSize: ".85rem" }}>{localToast.msg}</p>
          </div>
        </div>
      )}
    </div>
  );
}
